import { ChangeEvent } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import {
  Button,
  Dialog,
  FormLabel,
  Group as ScreentoneGroup,
  Switch,
  Token,
  Typography,
  useModalPortal,
  useModalState,
  Wrapper
} from '@screentone/core';
import { cloneDeep } from 'lodash';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import type { Container, ExtraProps, GroupContainer } from 'contexts/datamodel/DataModelContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DROPPABLE_ID_PREFIXES, DROPPABLE_TYPES } from 'contexts/drag-and-drop/dragUtils';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDragAndDrop } from 'contexts/drag-and-drop/useDragAndDrop';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import {
  ArticleItem,
  ModuleContainer,
  MultiZoneModuleContainer,
  PageModule,
  StructureGroupConfig
} from 'data/generated/graphql';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import {
  StashedModuleSnapshot,
  useScratchpadContext
} from 'features/section-page-edit/components/scratch-pad/scratchpad-context';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { convertPageModuleItemsToDataModelEntities } from 'utils/allesseh';
import { MULTIZONE_MODULE } from 'utils/constants';
import { getTreatmentType } from 'utils/modules';
import styles from './Group.module.scss';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

interface GroupProps extends CommonEntityProps {
  data: GroupContainer;
  extraProps: ExtraProps | undefined;
}

export const Group = ({ data: group, parentHierarchyId, index, isHistory, extraProps, isReadOnly }: GroupProps) => {
  const { generateHierarchyId, renderEntity, modifyEntity, cleanupDedupeForGroup, getGroupConfig } =
    useDataModelContext();
  const { currentLayout, pageTypeSetting } = usePageTypeSettingsContext();
  const { currentSourceDroppableId } = useDragAndDrop();
  const scratchpadCtx = useScratchpadContext();
  const { open: isConfirmOpen, openModal: openConfirmDialog, closeModal: closeConfirmDialog } = useModalState();
  const { renderNode: modalRenderNode } = useModalPortal();

  if (index && extraProps?.group?.groupsItemCount?.[index] === undefined) {
    throw new Error(
      `groupsItemCount is undefined for index ${index} in extraProps. Ensure that the group has been initialized correctly.`
    );
  }

  const isScratchpadEnabled = localStorage.getItem('scratchpadEnabled') === 'true';
  const hierarchyId = generateHierarchyId(group, parentHierarchyId, index);
  // SETTINGS_ONLY(structure-migration): settingsGroupConfig comes from hardcoded SETTINGS; remove once all pubs are migrated
  const settingsGroupConfig = currentLayout?.groupConfigs?.find((gc) => gc.id === group.groupId);
  // END SETTINGS_ONLY(structure-migration)
  const structureGroupConfig = getGroupConfig(group.groupId ?? '') as StructureGroupConfig | undefined;
  // structureGroupConfig (structure in DynamoDB — new data shape) takes priority.
  // settingsGroupConfig (hardcoded SETTINGS) is the fallback for pubs not yet migrated.
  const groupConfig = structureGroupConfig ?? settingsGroupConfig; // remove ?? settingsGroupConfig when SETTINGS_ONLY is deleted
  const canToggle = groupConfig?.canToggle ?? false;
  const groupDisplayLabel = groupConfig?.label ?? group.groupId ?? 'group';

  const childrenElements = group.collection.map((entity, i) => {
    const previousItems = extraProps!.group!.groupsItemCount!.slice(0, index).reduce((acc, item) => acc + item, 0);
    const childIndex = previousItems + i;

    return (
      <div key={i} className={styles.childWrapper} data-testid="group-child" data-child-index={childIndex}>
        {renderEntity(entity, {
          hierarchyId: parentHierarchyId!,
          index: childIndex,
          withoutDragNDrop: group.collection.length === 1,
          isHistory,
          extraProps,
          isReadOnly
        })}
      </div>
    );
  });

  const droppablePrefix = isHistory ? DROPPABLE_ID_PREFIXES.GROUP_HISTORY : DROPPABLE_ID_PREFIXES.GROUP;
  const isDifferentGroup = currentSourceDroppableId !== droppablePrefix + hierarchyId; // dropping modules onto another group is not allowed
  const isDropDisabled = !!isHistory || isDifferentGroup || isReadOnly;

  // Set isGroupEnabled for all entities in the group
  // Ensuring that there are no isGroupEnabled properties with a value of true in some entities and false in others
  const handleSwitchChange = (isEnabled: boolean) => {
    // Before mutating, snapshot module state into the scratchpad so an accidental toggle-off
    // doesn't destroy editor work (ET2-2458). Regular modules stash their collection; multi-zone
    // modules stash both the flat collection and every zone's contentIndexes so zone assignments
    // can be restored intact.
    if (!isEnabled && scratchpadCtx) {
      const stashedModules: StashedModuleSnapshot[] = [];
      const childHierarchyIds: string[] = [];

      group.collection.forEach((entity, i) => {
        const previousItems = extraProps!.group!.groupsItemCount!.slice(0, index).reduce((acc, item) => acc + item, 0);
        const childIndex = previousItems + i;
        const childHierarchyId = generateHierarchyId(entity, parentHierarchyId, childIndex);
        childHierarchyIds.push(childHierarchyId);

        if (entity.type !== EntityType.Module) return;
        const moduleEntity = entity as ModuleContainer;

        if (moduleEntity.subtype === MULTIZONE_MODULE) {
          const mzEntity = moduleEntity as MultiZoneModuleContainer;
          if (mzEntity.collection.length === 0) return;

          stashedModules.push({
            kind: 'multizone',
            moduleHierarchyId: childHierarchyId,
            treatmentType: getTreatmentType(mzEntity.attributes.pageModule as PageModule) ?? '',
            collection: cloneDeep(mzEntity.collection) as ArticleItem[],
            zones: (mzEntity.attributes.zones ?? []).map((z) => ({
              name: z.name,
              contentIndexes: [...(z.contentIndexes ?? [])]
            }))
          });
          return;
        }

        stashedModules.push({
          kind: 'regular',
          moduleHierarchyId: childHierarchyId,
          treatmentType: getTreatmentType(moduleEntity.attributes.pageModule as PageModule) ?? '',
          items: cloneDeep(moduleEntity.collection) as ArticleItem[]
        });
      });

      if (stashedModules.length > 0) {
        scratchpadCtx.stashGroupItems({
          groupHierarchyId: hierarchyId,
          groupId: group.groupId ?? '',
          groupLabel: groupConfig?.label ?? group.groupId ?? '',
          childHierarchyIds,
          modules: stashedModules
        });
      }
    }

    group.collection.forEach((entity, i) => {
      const previousItems = extraProps!.group!.groupsItemCount!.slice(0, index).reduce((acc, item) => acc + item, 0);
      const childIndex = previousItems + i;
      const hierarchyId = generateHierarchyId(entity, parentHierarchyId, childIndex);
      modifyEntity(hierarchyId, (entity) => {
        // eslint-disable-next-line no-param-reassign
        (entity as Container).isGroupEnabled = isEnabled;
      });
    });

    // If the group is disabled and all entities are modules, reset modules in group to default
    const allGroupEntitiesAreModules = group.collection.every((entity) => entity.type === EntityType.Module);
    if (!isEnabled && allGroupEntitiesAreModules) {
      // Get the treatment types of the modules in the group
      const groupTreatmentTypes = group.collection.map((entity) => {
        const module = entity as ModuleContainer;

        return getTreatmentType(module.attributes.pageModule as PageModule);
      });

      // Get the all the treatment types from the page type settings
      const allTreatmentTypes = pageTypeSetting!.treatmentTypeSettings;

      // Get the default modules for the treatment types in the group
      const groupDefaultModules = allTreatmentTypes
        .filter((treatmentType) => groupTreatmentTypes.includes(treatmentType.treatmentType))
        .map((treatmentType) => treatmentType.module);

      // Remove deduped exclusions for all modules in one pass
      const moduleHierarchyIds = group.collection.map((entity, i) => {
        const previousItems = extraProps!.group!.groupsItemCount!.slice(0, index).reduce((acc, item) => acc + item, 0);
        const childIndex = previousItems + i;
        return generateHierarchyId(entity, parentHierarchyId, childIndex);
      });
      cleanupDedupeForGroup(moduleHierarchyIds);

      // Reset each module in the group to its default module
      groupDefaultModules.forEach((defaultModule, i) => {
        const previousItems = extraProps!.group!.groupsItemCount!.slice(0, index).reduce((acc, item) => acc + item, 0);

        const childIndex = previousItems + i;
        const hierarchyId = generateHierarchyId(group.collection[i], parentHierarchyId, childIndex);

        const collection = convertPageModuleItemsToDataModelEntities(defaultModule as PageModule);

        modifyEntity(hierarchyId, (entity) => {
          const moduleContainer = entity as ModuleContainer;
          moduleContainer.collection = collection;

          if (moduleContainer.subtype === MULTIZONE_MODULE) {
            const multizoneModule = moduleContainer as MultiZoneModuleContainer;

            multizoneModule.attributes.zones?.forEach((zone) => {
              // eslint-disable-next-line no-param-reassign
              zone.contentIndexes = [];
            });
          }
        });
      });
    }
  };

  // A toggle-off will destroy or stash content. Confirm first when the group actually holds items,
  // so a fat-finger click can't nuke editor work silently.
  const groupHasItems = group.collection.some((entity) => {
    if (entity.type !== EntityType.Module) return false;
    return (entity as ModuleContainer).collection.length > 0;
  });

  const handleToggleIntent = (isEnabled: boolean) => {
    if (isEnabled || !groupHasItems || !scratchpadCtx || !isScratchpadEnabled) {
      handleSwitchChange(isEnabled);

      return;
    }

    openConfirmDialog();
  };

  const handleConfirmDisable = () => {
    closeConfirmDialog();
    handleSwitchChange(false);
  };

  const dialogTitleId = `group-disable-dialog-title-${group.groupId ?? 'group'}`;
  const dialogDescId = `group-disable-dialog-desc-${group.groupId ?? 'group'}`;

  if (!group.isGroupEnabled && isHistory) {
    return null;
  }

  return (
    <Droppable
      data-testid="group-droppable"
      droppableId={droppablePrefix + hierarchyId}
      type={DROPPABLE_TYPES.GROUP}
      isDropDisabled={isDropDisabled}
    >
      {(provided) => (
        <div
          data-testid="group-container"
          className={styles.groupWrapper}
          data-model-hierarchy-id={hierarchyId}
          data-group-id={group.groupId}
          data-is-history={isHistory ? 'true' : 'false'}
          ref={provided.innerRef}
          {...provided.droppableProps}
        >
          <Wrapper data-testid="group-wrapper-inner">
            <ScreentoneGroup data-testid="group-header" align="space-between">
              {groupConfig?.label && (
                <Typography
                  data-testid="group-label"
                  className={styles.groupLabel}
                  variant="label2"
                  margin={{ bottom: 'none' }}
                >
                  {groupConfig.label.toUpperCase()}
                  {groupConfig.showModuleCount && (
                    <Token
                      data-testid="group-count-token"
                      className={styles.groupLabel}
                      margin={{ bottom: 'none', left: 'xs' }}
                    >
                      {`${group.collection.length} packages`}
                    </Token>
                  )}
                </Typography>
              )}
              {canToggle && !isHistory && (
                <FormLabel
                  key="Enable"
                  label="Enable"
                  labelPosition="right"
                  className={styles.enableSwitchLabel}
                  data-testid="group-toggle-label"
                >
                  <Switch
                    name={group.groupId}
                    id={group.groupId}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      handleToggleIntent(e.target.checked);
                    }}
                    checked={group.isGroupEnabled}
                    disabled={isReadOnly}
                    data-testid="group-toggle-switch"
                  />
                </FormLabel>
              )}
              {canToggle ? (group.isGroupEnabled ? childrenElements : null) : childrenElements}
            </ScreentoneGroup>
          </Wrapper>
          <Dialog
            status={isConfirmOpen ? 'open' : 'closed'}
            size="sm"
            aria-labelledby={dialogTitleId}
            aria-describedby={dialogDescId}
            onDismiss={closeConfirmDialog}
            renderNode={modalRenderNode}
            data-testid="group-disable-confirm-dialog"
          >
            <Dialog.Title id={dialogTitleId}>{`Disable ${groupDisplayLabel}?`}</Dialog.Title>
            <Dialog.Content id={dialogDescId}>
              Items in this group will be moved to the Scratch Pad. You can restore them from there.
            </Dialog.Content>
            <Dialog.Actions>
              <Button secondary onClick={closeConfirmDialog} data-testid="group-disable-cancel">
                Cancel
              </Button>
              <Button primary onClick={handleConfirmDisable} data-testid="group-disable-confirm">
                Disable
              </Button>
            </Dialog.Actions>
          </Dialog>
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};
